/**
 * Trial status shown in Settings and, in the last 3 days, on the dashboard.
 * trial_ends_at is an instant. Stripe's trial_period_days ends that same
 * instant 14 days later, not at UTC midnight. The date shown is that instant
 * in the viewer's time zone, or America/New_York when the zone is unknown.
 */

const { checkoutPlanLabel, isSoloPlanName, isTeamPlanName } = require('./planNames');

const DAY_MS = 24 * 60 * 60 * 1000;
const TRIAL_BANNER_DAYS = 3;
const FALLBACK_TIME_ZONE = 'America/New_York';

function trialEndDate(value) {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return '';
  }
}

function timeZoneWorks(zone) {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone }).format(new Date());
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * An explicit zone wins. Otherwise a browser uses its own zone. A server,
 * or an invalid zone, uses America/New_York so UTC midnight is not the date.
 */
function trialDisplayTimeZone(timeZone) {
  const requested = String(timeZone || '').trim();
  if (requested && timeZoneWorks(requested)) return requested;
  if (!requested && typeof window !== 'undefined') {
    const browser = browserTimeZone();
    if (browser && timeZoneWorks(browser)) return browser;
  }
  return FALLBACK_TIME_ZONE;
}

/** Same instant Stripe stores for trial_period_days: now plus N times 24 hours. */
function addTrialDays(value, days) {
  const date = trialEndDate(value);
  const count = Number(days);
  if (!date || !Number.isFinite(count)) return null;
  return new Date(date.getTime() + count * DAY_MS);
}

function formatTrialDate(value, timeZone, month) {
  const date = trialEndDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    month,
    day: 'numeric',
    year: 'numeric',
    timeZone: trialDisplayTimeZone(timeZone),
  }).format(date);
}

function formatTrialEnd(value, timeZone) {
  return formatTrialDate(value, timeZone, 'short');
}

function formatTrialLongDate(value, timeZone) {
  return formatTrialDate(value, timeZone, 'long');
}

function formatPlanPrice(price) {
  if (price == null || price === '') return '';
  const amount = Number(price);
  if (!Number.isFinite(amount) || amount < 0) return '';
  if (Number.isInteger(amount)) return `$${amount}`;
  return `$${amount.toFixed(2)}`;
}

/** Charge copy always shows cents, so $49 is $49.00. */
function formatChargeAmount(price) {
  if (price == null || price === '') return '';
  const amount = Number(price);
  if (!Number.isFinite(amount) || amount < 0) return '';
  return `$${amount.toFixed(2)}`;
}

/**
 * A Stripe customer id is created before checkout and is not a card.
 * A subscription means Checkout collected one. A default payment method
 * counts too, including the billing-customer lookup (remote).
 */
function trialHasPaymentMethod(profile, remote) {
  if (profile && profile.stripe_subscription_id) return true;
  if (profile && (profile.default_payment_method || profile.has_default_payment_method)) return true;
  if (remote && (remote.hasDefaultPaymentMethod || remote.hasSubscription)) return true;
  return false;
}

/** Update-subscription is only valid when Stripe already has a subscription. */
function profileHasSubscription(profile, remote) {
  if (profile && profile.stripe_subscription_id) return true;
  return Boolean(remote && remote.hasSubscription);
}

function trialPlanName(planName, agents) {
  const name = String(planName || '').trim();
  if (!name) return 'your plan';
  if (isSoloPlanName(name) || isTeamPlanName(name)) return checkoutPlanLabel(name, agents);
  return name;
}

function monthlyPlanPrice(profile, teamPricing, seats) {
  if (!profile) return null;
  if (profile.subscription_plan === 'solo') return 49;
  if (profile.subscription_plan !== 'team') return null;
  // client_limit grows with the homeowner add-on. Seats come from max_seats
  // or the subscription quantity, never from that limit.
  const agents = Math.floor(Number(seats));
  if (!Number.isFinite(agents) || agents < 2) return null;
  const plans = Array.isArray(teamPricing) ? teamPricing : [];
  const match = plans.find((plan) => plan.agents === agents);
  return match ? match.price : null;
}

function trialDaysRemaining(value, now = new Date()) {
  const end = trialEndDate(value);
  const at = now instanceof Date ? now : new Date(now);
  if (!end || Number.isNaN(at.getTime())) return null;
  return Math.ceil((end.getTime() - at.getTime()) / DAY_MS);
}

function trialBannerDue(value, now = new Date()) {
  const days = trialDaysRemaining(value, now);
  return days !== null && days <= TRIAL_BANNER_DAYS;
}

function trialStatusCopy({
  status,
  trialEndsAt,
  price,
  planName,
  hasCard,
  omitCharge,
  now,
  timeZone,
} = {}) {
  if (status !== 'trialing') return null;
  const dateLabel = formatTrialEnd(trialEndsAt, timeZone);
  const showBanner = dateLabel ? trialBannerDue(trialEndsAt, now) : false;
  if (omitCharge) {
    const detail = dateLabel ? `Free trial ends ${dateLabel}.` : 'Free trial.';
    return { headline: '', detail, message: detail, hasCard: false, showBanner, dateLabel };
  }
  if (!hasCard) {
    const detail = dateLabel
      ? `Free trial ends ${dateLabel}. Add a payment method to continue after your trial.`
      : 'Free trial. Add a payment method to continue after your trial.';
    return { headline: '', detail, message: detail, hasCard: false, showBanner, dateLabel };
  }
  const priceLabel = formatChargeAmount(price) || 'the plan price';
  const name = trialPlanName(planName);
  const planLabel = /\bplan$/i.test(name) ? name : `${name} plan`;
  const before = dateLabel || 'the trial ends';
  const detail = dateLabel
    ? `Free trial ends ${dateLabel}. On that date your card will be charged ${priceLabel} for the ${planLabel}, and then every month at the then-current price, until you cancel. Cancel anytime online here. You won't be charged if you cancel before ${dateLabel}.`
    : `Free trial. Your card will be charged ${priceLabel} for the ${planLabel} when the trial ends, and then every month at the then-current price, until you cancel. Cancel anytime online here. You won't be charged if you cancel before ${before}.`;
  return { headline: '', detail, message: detail, hasCard: true, showBanner, dateLabel };
}

module.exports = {
  TRIAL_BANNER_DAYS,
  FALLBACK_TIME_ZONE,
  trialDisplayTimeZone,
  addTrialDays,
  formatTrialEnd,
  formatTrialLongDate,
  formatPlanPrice,
  formatChargeAmount,
  trialHasPaymentMethod,
  profileHasSubscription,
  trialPlanName,
  monthlyPlanPrice,
  trialDaysRemaining,
  trialBannerDue,
  trialStatusCopy,
};
